import { legendProportional } from './legendProportional.js';
import { eventBus } from '../eventBus.js';
import { legendChoropleth } from './legendChoropleth.js';
import { svg } from '../utils/svg.js';
import { number } from '../utils/number.js';
import { dataSeries } from '../dataSeries.js';

/**
 * @module gui/legend
 */
export const legend = {

  /**
   * The id of the dom element containing the legend.
   * @type {String}
   * @private
   */
  domId: 'legend',

  /**
   * DOM element of the div containing the legend
   * @type {?HTMLDivElement}
   * @private
   */
  domNode: null,

  /**
   * ID of the div containing the legend data
   * @type {String}
   * @private
   */
  contentNodeId: 'legend-data',

  /**
   * DOM element of the div containing the legend data
   * @type {?HTMLDivElement}
   * @private
   */
  contentNode: null,

  /**
   * ID of the div containing the value for whole switzerland
   * @type String
   * @private
   */
  _rowTotalDivId: 'row-total',

  /**
   * DOM element of the div containing the value for whole switzerland
   * @type {?HTMLDivElement}
   * @private
   */
  _rowNodeTotal: null,

  /**
   * @type {JsonStatReader} NAFIDAS JSON-stat reader
   */
  reader: null,

  /**
   * @type {?DataSeries}
   */
  rowData: null,

  /**
   * Initialize the module
   * @param {JsonStatReader} jsonStatReader
   * @mixes module:legendProportional|module:legendChoropleth
   * @returns {void}
   */
  init (jsonStatReader) {
    this.reader = jsonStatReader;
    this._mixin();
    this._initDom();
    this._initEvents();
  },

  /**
   * mixin methods and properties.
   * @private
   */
  _mixin () {
    let module = this.reader.isChoropleth() ? legendChoropleth : legendProportional;
    const fnc1 = this._initDom;
    const fnc2 = module._initDom;
    Object.assign(this, module);
    this._initDom = () => {
      fnc1.call(this);
      fnc2.call(this);
    };
  },

  /**
   * Create the DOM of the legend.
   * @private
   */
  _initDom () {
    this.contentNode = document.getElementById(this.contentNodeId);
    this.domNode = document.getElementById(this.domId);

    this._rowNodeTotal = document.createElement('div');
    this._rowNodeTotal.id = this._rowTotalDivId;
    this._rowNodeTotal.classList.add('legend__row-value');
    this.contentNode.append(this._rowNodeTotal);

    const titleH3 = document.createElement('h3');
    titleH3.innerText = this.reader.getLabel(this.reader.getDimIdMetric());
    this.domNode.append(titleH3);

    const unitP = document.createElement('p');
    unitP.innerText = this.reader.getTransl('Einheit') + ': ' + this.reader.getValueUnit();
    this.domNode.append(unitP);
    this.domNode.append(this.contentNode);
  },

  /**
   * Initialize the event listeners
   * @listens eventBus~event:regionEnter
   * @listens eventBus~event:regionLeave
   * @returns {void}
   * @private
   */
  _initEvents () {
    eventBus.add('regionEnter', this.highlight.bind(this));
    eventBus.add('regionLeave', this.unhighlight.bind(this));
  },

  /**
   * Update the choropleth (table) or proportional (circle) legend
   * @returns {void}
   * @param {DataSeries} rowData
   */
  update (rowData) {
    let legend;
    const total = {
      label: rowData.total.label,
      value: rowData.total.value,
      error: rowData.total.error
    };
    this._rowNodeTotal.innerHTML = dataSeries.createStr(total);
    if (this.contentNode.firstChild.id !== this._rowTotalDivId) { // false only on initial update
      this.contentNode.firstChild.remove();
    }
    legend = this.create(rowData);
    this.contentNode.firstChild.before(legend);
    this.rowData = rowData;
  },

  /**
   * Calculate the height of the text.
   * Note: works only if the element is placed part of the DOM.
   * @param {SVGElement} svg
   * @return {number} the height of the first text element.
   * @private
   */
  _calcTextHeight (svg) {
    const bbox = svg.querySelector('text').getBBox();
    return number.roundTo(bbox.height, 1);
  },

  /**
   * Export the legend as SVG graphic.
   * @return {SVGSVGElement}
   */
  exportSvg () {
    let x, y, text;
    const g = svg.create('g', {
      id: 'legend'
    });
    // add legend title
    let nodes = this.domNode.children;
    x = 0;
    y = 12;
    text = svg.createText(0, y, nodes[0].innerHTML);
    svg.setAttributes(text, {
      'font-weight': 'bold'
    });
    // add legend unit
    g.append(text);
    x = 0;
    y += 20;
    text = svg.createText(0, y, nodes[1].innerHTML);
    g.append(text);

    // add legend data
    const gChild = this._exportDataSvg();
    y += 12;
    gChild.setAttribute('transform', 'translate(' + 0 + ', ' + y + ')');
    g.append(gChild);

    return g;
  },

  /**
   * Create the legend.
   * Note: This method will be overwritten by mixing in
   *    the module legendProportional/legendChoropleth in the init method.
   * @param {DataSeries} data
   */
  create (data) {},

  /**
   * Export the svg.
   * Note: will be overwritten by the mixin
   * @return {SVGGElement}
   * @private
   */
  _exportDataSvg () {},

  _init () {}
};